import DownloadIcon from '@mui/icons-material/Download';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableRowsIcon from '@mui/icons-material/TableRows';
import Box from '@mui/material/Box';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import TablePagination from '@mui/material/TablePagination';
import Tooltip from '@mui/material/Tooltip';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CreateTimeEntryDto, Project, UpdateTimeEntryDto } from 'api';
import { reportsService, teamService, timeEntryService } from 'api';
import { useAuthStore } from 'features/auth/stores/authStore';
import { useSnackbar } from 'notistack';
import React, { useCallback, useMemo, useState } from 'react';
import { DataTable } from 'shared/components/DataTable';
import type { DataTableColumn } from 'shared/components/DataTable';
import { queryKeys } from 'shared/constants/queryKeys';
import { usePermissions } from 'shared/hooks/usePermissions';
import { svgToPngBase64 } from 'shared/utils/pdfBuilder';

import {
  ExportMenu,
  ExportMenuIcon,
  DetailedUserName,
  DetailedDuration,
  DetailedActionIcon,
  DetailedDeleteIcon,
  DetailedTabRoot,
  DetailedTableWrapper,
  DetailedTableInner,
  detailedTimeCellSx,
  detailedUserNameSx,
} from '../reports.styles';
import {
  handleExportCSV as exportToCSV,
  handleExportExcel as exportToExcel,
  handleDetailedExportPDFv2,
} from '../utils/exportHelpers';
import { formatDuration, DetailedEntry, flattenDetailedReportData } from '../utils/reportsUtils';

import { AddTimeEntryModal } from './AddTimeEntryModal';
import { EditableTimeCell } from './EditableTimeCell';
import { EditableTitleCell } from './EditableTitleCell';

interface DetailedTabProps {
  startDate: string;
  endDate: string;
  filters: {
    userIds?: string[];
    clientIds?: string[];
    projectIds?: string[];
    billable?: boolean | null;
    hasDescription?: boolean;
    descriptionSearch?: string;
  };
  projects: Project[];
  clients: Array<{ id: string; name: string }>;
  exportAnchorEl?: HTMLElement | null;
  onExportMenuClose?: () => void;
  addEntryOpen?: boolean;
  onAddEntryClose?: () => void;
}

// ─── DetailedTab ──────────────────────────────────────────────────────────────

export const DetailedTab: React.FC<DetailedTabProps> = ({
  startDate,
  endDate,
  filters,
  projects,
  clients,
  exportAnchorEl,
  onExportMenuClose,
  addEntryOpen = false,
  onAddEntryClose,
}) => {
  const { canEditReportEntries, canCreateEntriesForOthers } = usePermissions();
  const currentUserId = useAuthStore((s) => s.user?.id ?? '');

  const [sortColumn, setSortColumn] = useState<string>('startedAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  // Single shared action menu: track which row is active + its anchor element
  const [actionMenu, setActionMenu] = useState<{ rowId: string; anchor: HTMLElement } | null>(null);

  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const handleExportMenuClose = () => {
    onExportMenuClose?.();
  };

  const apiParams = useMemo(
    () => ({
      from: startDate,
      to: endDate,
      userIds: filters.userIds?.join(','),
      projectIds: filters.projectIds?.join(','),
      clientIds: filters.clientIds?.join(','),
      isBillable: filters.billable === null ? undefined : filters.billable,
      hasDescription: filters.hasDescription,
      descriptionSearch: filters.descriptionSearch || undefined,
    }),
    [startDate, endDate, filters],
  );

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTimeEntryDto }) =>
      timeEntryService.updateTimeEntry(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.reports.detailed(apiParams) });
      enqueueSnackbar('Entry updated', { variant: 'success' });
    },
    onError: () => enqueueSnackbar('Failed to update entry', { variant: 'error' }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => timeEntryService.deleteTimeEntry(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.reports.detailed(apiParams) });
      enqueueSnackbar('Entry deleted', { variant: 'success' });
    },
    onError: () => enqueueSnackbar('Failed to delete entry', { variant: 'error' }),
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateTimeEntryDto) => timeEntryService.createTimeEntry(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.reports.detailed(apiParams) });
      enqueueSnackbar('Entry added', { variant: 'success' });
    },
    onError: () => enqueueSnackbar('Failed to add entry', { variant: 'error' }),
  });

  const handleUpdate = useCallback(
    (id: string, data: UpdateTimeEntryDto) => {
      updateMutation.mutate({ id, data });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [updateMutation.mutate],
  );

  const handleDelete = useCallback(
    (id: string) => {
      setActionMenu(null);
      deleteMutation.mutate(id);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [deleteMutation.mutate],
  );

  const handleActionMenuOpen = useCallback((rowId: string, anchor: HTMLElement) => {
    setActionMenu({ rowId, anchor });
  }, []);

  const handleActionMenuClose = useCallback(() => {
    setActionMenu(null);
  }, []);

  const handleSort = useCallback(
    (columnId: string) => {
      if (sortColumn === columnId) {
        setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortColumn(columnId);
        setSortDirection('asc');
      }
      setPage(0);
    },
    [sortColumn],
  );

  const { data: detailedData, isLoading } = useQuery({
    queryKey: queryKeys.reports.detailed(apiParams),
    queryFn: () => reportsService.getDetailedReport(apiParams).then((res: any) => res.data.data),
    enabled: !!startDate,
  });

  const totalDuration = detailedData?.totalDurationSecs ?? 0;
  const billableDuration = detailedData?.billableDurationSecs ?? 0;
  const amount = detailedData?.amountCents ?? 0;

  const { data: teamMembers = [] } = useQuery({
    queryKey: queryKeys.team.members(),
    queryFn: () => teamService.getMembers().then((r) => r.data.data),
  });

  const userMap = useMemo(() => {
    const map = new Map<string, string>();
    teamMembers.forEach((member: any) => {
      map.set(member.userId, member.name || member.email);
    });
    return map;
  }, [teamMembers]);

  const emailMap = useMemo(() => {
    const map = new Map<string, string>();
    teamMembers.forEach((member: any) => {
      map.set(member.userId, member.email || '');
    });
    return map;
  }, [teamMembers]);

  const projectMap = useMemo(() => {
    const map = new Map<string, { name: string; color: string }>();
    projects.forEach((p) => {
      map.set(p.id, { name: p.name, color: p.color });
    });
    return map;
  }, [projects]);

  const clientMap = useMemo(() => {
    const map = new Map<string, string>();
    clients.forEach((c) => {
      map.set(c.id, c.name);
    });
    return map;
  }, [clients]);

  // Flatten data
  const allRows: DetailedEntry[] = useMemo(() => {
    return flattenDetailedReportData(
      detailedData?.days ?? [],
      userMap,
      projectMap,
      clientMap,
      emailMap,
    );
  }, [detailedData, userMap, projectMap, clientMap, emailMap]);

  // DataTable column definitions
  const columns: DataTableColumn<DetailedEntry>[] = useMemo(
    () => [
      {
        id: 'title',
        label: 'Title',
        sortable: true,
        sortValue: (row) => row.description || '',
        render: (row) => (
          <EditableTitleCell
            entry={row}
            onUpdate={canEditReportEntries ? handleUpdate : undefined}
          />
        ),
      },
      {
        id: 'userName',
        label: 'User',
        width: 220,
        sortable: true,
        sortValue: (row) => row.userName || '',
        render: (row) => (
          <Tooltip title={row.userName || ''} placement="top">
            <DetailedUserName sx={detailedUserNameSx}>{row.userName}</DetailedUserName>
          </Tooltip>
        ),
      },
      {
        id: 'startedAt',
        label: 'Time',
        width: 200,
        sortable: true,
        sortValue: (row) => row.startedAt,
        render: (row) => (
          <Box sx={detailedTimeCellSx}>
            <EditableTimeCell
              entry={row}
              onUpdate={canEditReportEntries ? handleUpdate : undefined}
            />
          </Box>
        ),
      },
      {
        id: 'durationSecs',
        label: 'Duration',
        width: 110,
        sortable: true,
        sortValue: (row) => row.durationSecs,
        render: (row) => <DetailedDuration>{formatDuration(row.durationSecs)}</DetailedDuration>,
      },
      ...(canEditReportEntries
        ? ([
            {
              id: 'actions',
              label: '',
              width: 50,
              render: (row: DetailedEntry) => (
                <Tooltip title="More options">
                  <DetailedActionIcon
                    as="span"
                    onClick={(e: React.MouseEvent<HTMLElement>) =>
                      handleActionMenuOpen(row.id, e.currentTarget)
                    }
                  >
                    <MoreVertIcon fontSize="small" />
                  </DetailedActionIcon>
                </Tooltip>
              ),
            },
          ] as DataTableColumn<DetailedEntry>[])
        : []),
    ],
    [handleUpdate, handleActionMenuOpen, canEditReportEntries],
  );

  const sortedRows = useMemo(() => {
    const col = columns.find((c) => c.id === sortColumn);
    if (!col?.sortValue) return allRows;
    return [...allRows].sort((a, b) => {
      const aVal = col.sortValue!(a);
      const bVal = col.sortValue!(b);
      const cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  }, [allRows, columns, sortColumn, sortDirection]);

  const paginatedRows = useMemo(
    () => sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sortedRows, page, rowsPerPage],
  );

  const handleExportCSV = () => {
    exportToCSV(allRows, { totalDuration, billableDuration, amount }, startDate, endDate);
  };

  const handleExportExcel = () => {
    exportToExcel(allRows, { totalDuration, billableDuration, amount }, startDate, endDate);
  };

  const handleExportPDF = async () => {
    let logoBase64: string | undefined;
    try {
      const svgRes = await fetch('/ZeLogLogo-192.svg');
      const svgText = await svgRes.text();
      const png = await svgToPngBase64(svgText, 128);
      if (png) logoBase64 = png;
    } catch (_) {
      // logo is optional — proceed without it
    }
    handleDetailedExportPDFv2({
      rows: allRows,
      summary: { totalDuration, billableDuration, amount },
      startDate,
      endDate,
      logoBase64,
    });
  };

  return (
    <DetailedTabRoot>
      {/* Export menu — triggered from hero Export button */}
      <Menu
        anchorEl={exportAnchorEl}
        open={Boolean(exportAnchorEl)}
        onClose={handleExportMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: 100 } } }}
      >
        <ExportMenu
          onClick={() => {
            handleExportCSV();
            handleExportMenuClose();
          }}
        >
          <ExportMenuIcon>
            <TableRowsIcon />
          </ExportMenuIcon>
          Export CSV
        </ExportMenu>
        <ExportMenu
          onClick={() => {
            handleExportExcel();
            handleExportMenuClose();
          }}
        >
          <ExportMenuIcon>
            <DownloadIcon />
          </ExportMenuIcon>
          Export Excel
        </ExportMenu>
        <ExportMenu
          onClick={() => {
            handleExportPDF();
            handleExportMenuClose();
          }}
        >
          <ExportMenuIcon>
            <PictureAsPdfIcon />
          </ExportMenuIcon>
          Export PDF
        </ExportMenu>
      </Menu>

      <DetailedTableWrapper>
        <DetailedTableInner>
          <DataTable<DetailedEntry>
            columns={columns}
            rows={paginatedRows}
            getRowId={(row) => row.id}
            loading={isLoading}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSort={handleSort}
            emptyMessage="No data for the selected date and filters."
            borderless
            headerFontSize="11px"
            headerWeight={500}
            headerColor="#5A5578"
            rowHoverColor="rgba(110, 79, 247, 0.03)"
          />
        </DetailedTableInner>
        <TablePagination
          component="div"
          count={sortedRows.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </DetailedTableWrapper>

      {/* Single shared action menu — only one Menu instance in the entire DOM */}
      <Menu
        anchorEl={actionMenu?.anchor ?? null}
        open={Boolean(actionMenu)}
        onClose={handleActionMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: { sx: { minWidth: 120, borderRadius: '10px', p: 0.5, boxShadow: '0 8px 24px rgba(15,23,42,0.12)' } },
          list: { sx: { py: 0 } },
        }}
      >
        <MenuItem
          onClick={() => {
            if (actionMenu) handleDelete(actionMenu.rowId);
          }}
          sx={{ fontSize: '0.8125rem', minHeight: 0, py: '5px', px: 1, borderRadius: '7px', gap: 1 }}
        >
          <DetailedDeleteIcon fontSize="small" sx={{ fontSize: 14 }} />
          Delete
        </MenuItem>
      </Menu>

      <AddTimeEntryModal
        open={addEntryOpen}
        onClose={() => onAddEntryClose?.()}
        onSubmit={(entries) => {
          entries.forEach((entry) => createMutation.mutate(entry));
          onAddEntryClose?.();
        }}
        teamMembers={teamMembers}
        projects={projects}
        currentUserId={currentUserId}
        canSelectUser={canCreateEntriesForOthers}
        defaultDate={startDate}
      />
    </DetailedTabRoot>
  );
};
